import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useFetchWithSpinner } from "../../hooks/useFetchWithSpinner";
import {
  BarChartSummary,
  BarChartComparison,
  VerticalPositiveNegativeBarChart,
  LineChartComparison,
  ComparativeTable
} from "../Charts";
import { selectDates, selectTopN, selectHurtoType, selectComparisonYear } from "../../redux/slices/filtersSlice";
import {
  filterData,
  processFilteredData,
  applyFilterLogic
} from "../../utils/filterFunctions";
import "./Pages.css";

const Hurtos = () => {
  const [data, setData] = useState([]);

  // Obtener filtros globales desde Redux
  const dates = useSelector(selectDates);
  const comparisonYear = useSelector(selectComparisonYear);
  const topN = useSelector(selectTopN);
  const hurtoType = useSelector(selectHurtoType);

  const baseFilters = {
    sexo: null,
    medio: null,
    comuna: null,
    mes: null,
    modalidad: null,
    rangoEdad: null,
    diaJornada: null,
  };
  const [filters, setFilters] = useState({ ...baseFilters });
  const [frozenKey, setFrozenKey] = useState(null);
  const fetchWithSpinner = useFetchWithSpinner();

  // useEffect que se dispara cuando cambian los filtros de fecha desde Redux
  useEffect(() => {
    if (dates.start && dates.end) {
      fetchWithSpinner({
        startDate: dates.start,
        endDate: dates.end,
        year_to_compare: comparisonYear,
        endpoint: 'hurtos/',
        hurto_type: hurtoType,
      }, setData);
    }
  }, [dates.start, dates.end, hurtoType, comparisonYear]);

  const currentYear = dates.end ? new Date(dates.end).getFullYear() : new Date().getFullYear();

  const filteredData = filterData(data, filters);

  const dias = [...new Set(data.map(h => h.dia))];
  const jornadas = [...new Set(data.map(h => h.jornada))];

  const results = processFilteredData(filteredData, {
    includeTotals: true,
    groupByFields: ["mes", "medio", "sexo", "comuna", "modalidad"],
    includeDayShift: true,
    dias,
    jornadas
  }, comparisonYear, currentYear);

  const { totals: { total1, total2, diferencia, variacion }, resumenData } = results;
  const mesData = results.groups.mes;

  const medioDataRaw = results.groups.medio.sort((a, b) =>
    (b[currentYear] + b[comparisonYear]) - (a[currentYear] + a[comparisonYear])
  );
  const medioData = medioDataRaw.slice(0, Math.min(topN, medioDataRaw.length));

  const sexoData = results.groups.sexo
    .filter(item => item.name === "Hombre" || item.name === "Mujer")
    .sort((a, b) => (b[currentYear] + b[comparisonYear]) - (a[currentYear] + a[comparisonYear]));

  const comunaData = results.groups.comuna.sort((a, b) =>
    (b[currentYear] + b[comparisonYear]) - (a[currentYear] + a[comparisonYear])
  );

  const modalidadDataRaw = results.groups.modalidad.sort((a, b) =>
    (b[currentYear] + b[comparisonYear]) - (a[currentYear] + a[comparisonYear])
  );
  const modalidadData = modalidadDataRaw.slice(0, Math.min(topN, modalidadDataRaw.length));

  const diasJornadaData1 = results.diasJornadaData[comparisonYear];
  const diasJornadaData2 = results.diasJornadaData[currentYear];

  const handleFilterClick = (tipo, valor, source) => {
    setFilters((prevFilters) =>
      applyFilterLogic(prevFilters, tipo, valor, source, frozenKey, setFrozenKey, baseFilters)
    );
  };

  return (
    <div className="dashboard-container">
      <div className="dashboard-grid">
        <div className="row top">
          <div className="card small">
            <BarChartSummary
              resumenData={resumenData}
              diferencia={diferencia}
              variacion={variacion}
              xKey="name"
              title="Resumen general"
              selectedKey={filters.año}
              frozenKey={frozenKey}
              onBarClick={(d) => handleFilterClick("año", d?.category, "Resumen general")}
            />
          </div>
          <div className="card small">
            <VerticalPositiveNegativeBarChart showData={sexoData} xKey="name" title="Sexo" selectedKey={filters.sexo} frozenKey={frozenKey} onBarClick={(d) => handleFilterClick("sexo", d?.category, "Sexo")} comparisonYear={comparisonYear} />
          </div>
          <div className="card wide">
            <BarChartComparison
              showData={modalidadData}
              xKey="name"
              title="Modalidad de los hurtos"
              selectedKey={filters.modalidad}
              frozenKey={frozenKey}
              onBarClick={(d) => handleFilterClick("modalidad", d?.category, "Modalidad de los hurtos")}
            />
          </div>
        </div>
        <div className="row middle">
          <div className="column-right">
            <div className="row bottom">
              <div className="card medium">
                <BarChartComparison
                  showData={medioData}
                  xKey="name"
                  title="Medio del hurto"
                  selectedKey={filters.medio}
                  frozenKey={frozenKey}
                  onBarClick={(d) => handleFilterClick("medio", d?.category, "Medio del hurto")}
                />
              </div>
              <div className="card medium">
                <ComparativeTable
                  previousData={diasJornadaData1}
                  previousYear={comparisonYear}
                  currentData={diasJornadaData2}
                  currentYear={currentYear}
                  title="Hurtos por día y jornada"
                  selectedKey={filters.diaJornada}
                  frozenKey={frozenKey}
                  onBarClick={(d) => handleFilterClick("diaJornada", d?.category, "Hurtos por día y jornada")}
                />
              </div>
            </div>
            <div className="row bottom">
              <div className="card medium">
                <LineChartComparison
                  showData={mesData}
                  xKey="name"
                  title="Hurtos por mes"
                  selectedKey={filters.mes}
                  frozenKey={frozenKey}
                  onBarClick={(d) => handleFilterClick("mes", d?.category, "Hurtos por mes")}
                />
              </div>
              <div className="card medium">
                <BarChartComparison
                  showData={comunaData}
                  xKey="name"
                  title="Hurtos por comuna"
                  selectedKey={filters.comuna}
                  frozenKey={frozenKey}
                  onBarClick={(d) => handleFilterClick("comuna", d?.category, "Hurtos por comuna")}
                />
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default Hurtos;
